import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";

const AddStoryModal = ({
  isOpen,
  onClose,
  onSubmit,
  currentUser,
  isLoading = false,
  uploadProgress = 0
}) => {
  const { getUserType } = useAuth();

  // Get user type color
  const currentUserType = getUserType();
  const getUserTypeColor = (userType) => {
    const userTypeConfig = {
      'PET_GUARDIAN': '#8548A8', // Purple
      'VETERINARIAN': '#00D4B3', // Cyan
      'SERVICE_PROVIDER': '#D65745', // Orange
      'SERVICE_PROVIDER': '#D65745', // Orange
      'CLINIC_MODERATOR': '#2A8AD2', // Blue
    };
    return userTypeConfig[userType] || '#8548A8'; // Default to purple
  };
  const userTypeColor = getUserTypeColor(currentUserType);

  const [selectedFiles, setSelectedFiles] = useState([])
  const [previewUrls, setPreviewUrls] = useState([])
  const [fileTypes, setFileTypes] = useState([])
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0)
  const [dragActive, setDragActive] = useState(false)
  const [isDraggingOrder, setIsDraggingOrder] = useState(false)
  const [error, setError] = useState('')
  const fileInputRef = useRef(null)
  const previewUrlsRef = useRef([])
  const { t } = useTranslation();

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      // Clean up all preview URLs
      previewUrlsRef.current.forEach(url => {
        if (url) URL.revokeObjectURL(url)
      })
      previewUrlsRef.current = []
      setSelectedFiles([])
      setPreviewUrls([])
      setFileTypes([])
      setCurrentMediaIndex(0)
      setError('')
    }
  }, [isOpen])

  // Helper function to check video duration
  const checkVideoDuration = (file) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        const duration = video.duration;

        // Check if duration exceeds 30 seconds
        if (duration > 30) {
          reject(new Error('Video duration exceeds 30 seconds'));
        } else {
          resolve(duration);
        }
      };

      video.onerror = () => {
        window.URL.revokeObjectURL(video.src);
        reject(new Error('Failed to load video metadata'));
      };

      video.src = URL.createObjectURL(file);
    });
  };

  // Handle file selection (single file only)
  const handleFilesSelect = async (files) => {
    setError('')

    if (!files || files.length === 0) return

    // Only take the first file
    const file = files[0]

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'video/mp4', 'video/avi', 'video/mov']
    if (!validTypes.includes(file.type)) {
      setError(`${file.name} is not supported`)
      return
    }

    // Validate file size (max 50MB)
    const maxSize = 50 * 1024 * 1024 // 50MB
    if (file.size > maxSize) {
      setError(`${file.name} is too large (max 50MB)`)
      return
    }

    // Check video duration (30 seconds max) for video files
    if (file.type.startsWith('video/')) {
      try {
        await checkVideoDuration(file);
      } catch (error) {
        setError(`${file.name} exceeds maximum duration of 30 seconds`)
        return
      }
    }

    // Clean up previous preview URL if any
    previewUrlsRef.current.forEach(url => {
      if (url) URL.revokeObjectURL(url)
    })

    const previewUrl = URL.createObjectURL(file)
    const fileType = file.type.startsWith('image/') ? 'image' : 'video'

    setSelectedFiles([file])
    setFileTypes([fileType])
    setPreviewUrls([previewUrl])
    previewUrlsRef.current = [previewUrl]
    setCurrentMediaIndex(0)
  }

  // Handle file input change
  const handleFileInputChange = (e) => {
    const files = e.target.files
    if (files && files.length > 0) {
      handleFilesSelect(files)
    }
  }

  // Handle drag and drop
  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      handleFilesSelect(files)
    }
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (selectedFiles.length === 0) {
      setError('Select at least one file')
      return
    }

    try {
      await onSubmit({
        files: selectedFiles,
        fileTypes
      })

      // Reset form on successful submission
      previewUrlsRef.current.forEach(url => {
        if (url) URL.revokeObjectURL(url)
      })
      previewUrlsRef.current = []
      setSelectedFiles([])
      setPreviewUrls([])
      setFileTypes([])
      setCurrentMediaIndex(0)
      setError('')
      onClose()
    } catch (error) {
      setError('Upload failed. Try again.')
    }
  }

  // Handle modal close
  const handleClose = () => {
    if (!isLoading) {
      // Clean up all preview URLs
      previewUrlsRef.current.forEach(url => {
        if (url) URL.revokeObjectURL(url)
      })
      previewUrlsRef.current = []
      onClose()
    }
  }

  const clearSelection = () => {
    // Clean up the preview URL
    previewUrlsRef.current.forEach(url => {
      if (url) URL.revokeObjectURL(url)
    })
    previewUrlsRef.current = []
    setSelectedFiles([])
    setPreviewUrls([])
    setFileTypes([])
    setCurrentMediaIndex(0)
  }

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) {
      handleClose()
    }
  }

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, isLoading, selectedFiles.length])



  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && !isLoading) {
      handleClose()
    }
  }

  if (!isOpen) return null

  const modalContent = (
    <>
      <style>
        {`
          @keyframes slideInFromTop {
            0% {
              opacity: 0;
              transform: translateY(-20px) scale(0.95);
            }
            100% {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
        `}
      </style>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.4)",
          backdropFilter: "blur(8px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
          padding: window.innerWidth < 768 ? "16px" : "20px",
          opacity: isOpen ? 1 : 0,
          transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
        }}
        onClick={handleBackdropClick}
      >
        <div
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(20px)",
            borderRadius: window.innerWidth < 768 ? "20px" : "24px",
            border: `1px solid rgba(255, 255, 255, 0.2)`,
            padding: window.innerWidth < 768 ? "24px" : "40px",
            maxWidth: window.innerWidth < 768 ? "100%" : "600px",
            width: "100%",
            maxHeight: "90vh",
            overflow: "auto",
            boxShadow: `
            0 25px 50px -12px rgba(0, 0, 0, 0.25),
            0 0 0 1px rgba(255, 255, 255, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.2)
          `,
            transform: isOpen ? "scale(1) translateY(0px)" : "scale(0.9) translateY(20px)",
            opacity: isOpen ? 1 : 0,
            transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
            fontFamily: '"Red Hat Display", sans-serif',
            position: "relative",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div style={{ marginBottom: "24px", textAlign: "center" }}>
            <h2
              id="modal-title"
              style={{
                fontSize: "24px",
                fontWeight: "600",
                color: "#1F2937",
                margin: "0 0 8px 0",
                fontFamily: '"Red Hat Display", sans-serif',
              }}
            >
              {t("addStoryModal.title")}
            </h2>
            <p
              id="modal-description"
              style={{
                fontSize: "14px",
                color: "#6B7280",
                margin: 0,
                fontFamily: '"Red Hat Display", sans-serif',
              }}
            >
              {t("addStoryModal.subtitle", "Share a photo or video")}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div
              style={{
                background: "linear-gradient(135deg, rgba(254, 242, 242, 0.9), rgba(254, 226, 226, 0.8))",
                backdropFilter: "blur(10px)",
                border: "2px solid rgba(220, 38, 38, 0.3)",
                borderRadius: "16px",
                padding: "16px 20px",
                marginBottom: "24px",
                color: "#DC2626",
                fontSize: "14px",
                fontFamily: '"Red Hat Display", sans-serif',
                fontWeight: "500",
                boxShadow: "0 4px 12px rgba(220, 38, 38, 0.1)",
                animation: "slideInFromTop 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "2px",
                  background: `linear-gradient(90deg, ${userTypeColor}, #DC2626)`,
                }}
              />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* File Upload Area */}
            <div style={{ marginBottom: "24px" }}>
              {selectedFiles.length === 0 ? (
                <div
                  role="button"
                  tabIndex={0}
                  aria-label="Upload media files"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      fileInputRef.current?.click();
                    }
                  }}
                  style={{
                    background: dragActive
                      ? `linear-gradient(135deg, rgba(255, 255, 255, 0.4), rgba(255, 255, 255, 0.1))`
                      : `linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.05))`,
                    backdropFilter: "blur(10px)",
                    border: `2px solid ${dragActive ? userTypeColor : "rgba(255, 255, 255, 0.3)"}`,
                    borderRadius: "20px",
                    padding: window.innerWidth < 768 ? "32px 20px" : "48px 24px",
                    textAlign: "center",
                    cursor: "pointer",
                    position: "relative",
                    overflow: "hidden",
                    transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                    transform: dragActive ? "scale(1.02)" : "scale(1)",
                    boxShadow: dragActive
                      ? `0 20px 40px rgba(0, 0, 0, 0.1), 0 0 0 1px ${userTypeColor}20`
                      : "0 8px 32px rgba(0, 0, 0, 0.1)",
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onMouseEnter={(e) => {
                    if (!dragActive) {
                      e.target.style.transform = "scale(1.01)";
                      e.target.style.boxShadow = "0 12px 40px rgba(0, 0, 0, 0.15)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!dragActive) {
                      e.target.style.transform = "scale(1)";
                      e.target.style.boxShadow = "0 8px 32px rgba(0, 0, 0, 0.1)";
                    }
                  }}
                >
                  <div
                    style={{
                      width: window.innerWidth < 768 ? "56px" : "64px",
                      height: window.innerWidth < 768 ? "56px" : "64px",
                      background: `linear-gradient(135deg, ${userTypeColor}20, ${userTypeColor}10)`,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 20px auto",
                      border: `2px solid ${userTypeColor}30`,
                      transition: "all 0.3s ease",
                      transform: dragActive ? "scale(1.1)" : "scale(1)",
                    }}
                  >
                    <svg
                      width={window.innerWidth < 768 ? "24" : "28"}
                      height={window.innerWidth < 768 ? "24" : "28"}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={userTypeColor}
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{
                        transition: "all 0.3s ease",
                        transform: dragActive ? "scale(1.1)" : "scale(1)",
                      }}
                    >
                      {/* Modern cloud upload icon */}
                      <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242" />
                      <path d="M12 12v9" />
                      <path d="m16 16-4-4-4 4" />
                    </svg>
                  </div>
                  <p
                    style={{
                      fontSize: "18px",
                      fontWeight: "600",
                      color: "#1F2937",
                      margin: "0 0 8px 0",
                      fontFamily: '"Red Hat Display", sans-serif',
                      transition: "all 0.3s ease",
                      transform: dragActive ? "scale(1.05)" : "scale(1)",
                    }}
                  >
                    {dragActive ? t("addStoryModal.dropHere", "Drop here") : t("addStoryModal.addPhotoOrVideo", "Add photo or video")}
                  </p>
                </div>
              ) : (
                <div
                  style={{
                    position: "relative",
                    borderRadius: "20px",
                    overflow: "hidden",
                    background: "linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                    transition: "all 0.3s ease",
                  }}
                >
                  {/* Main Media Display */}
                  <div style={{ position: "relative", height: "300px" }}>
                    {fileTypes[currentMediaIndex] === "image" ? (
                      <img
                        src={previewUrls[currentMediaIndex]}
                        alt={`Preview ${currentMediaIndex + 1}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          display: "block",
                        }}
                      />
                    ) : (
                      <video
                        src={previewUrls[currentMediaIndex]}
                        controls
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    )}



                    {/* Remove Current File Button */}
                    <button
                      type="button"
                      onClick={clearSelection}
                      aria-label="Remove"
                      style={{
                        position: "absolute",
                        top: "16px",
                        right: "16px",
                        width: "36px",
                        height: "36px",
                        borderRadius: "50%",
                        background: "rgba(0, 0, 0, 0.6)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(255, 255, 255, 0.2)",
                        color: "white",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "18px",
                        fontWeight: "600",
                        transition: "all 0.3s ease",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = "scale(1.1)";
                        e.target.style.background = "rgba(220, 38, 38, 0.8)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = "scale(1)";
                        e.target.style.background = "rgba(0, 0, 0, 0.6)";
                      }}
                    >
                      ×
                    </button>


                  </div>




                </div>
              )}



              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                onChange={handleFileInputChange}
                style={{ display: "none" }}
              />
            </div>



            {/* Action Buttons */}
            <div
              style={{
                display: "flex",
                gap: window.innerWidth < 768 ? "8px" : "12px",
                justifyContent: "flex-end",
                flexDirection: window.innerWidth < 768 ? "column" : "row",
              }}
            >
              <button
                type="button"
                onClick={handleClose}
                disabled={isLoading}
                style={{
                  padding: "14px 28px",
                  border: "2px solid rgba(255, 255, 255, 0.3)",
                  borderRadius: "12px",
                  background: "rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(10px)",
                  color: "#374151",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: isLoading ? "not-allowed" : "pointer",
                  fontFamily: '"Red Hat Display", sans-serif',
                  opacity: isLoading ? 0.6 : 1,
                  transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)";
                    e.target.style.transform = "scale(1.05)";
                    e.target.style.boxShadow = "0 8px 20px rgba(0, 0, 0, 0.15)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isLoading) {
                    e.target.style.backgroundColor = "rgba(255, 255, 255, 0.1)";
                    e.target.style.transform = "scale(1)";
                    e.target.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.1)";
                  }
                }}
              >
                {t("addStoryModal.cancel")}
              </button>
              <button
                type="submit"
                disabled={isLoading || selectedFiles.length === 0}
                style={{
                  padding: "14px 28px",
                  border: "none",
                  borderRadius: "12px",
                  background: isLoading || selectedFiles.length === 0
                    ? "linear-gradient(135deg, #D1D5DB, #9CA3AF)"
                    : `linear-gradient(135deg, ${userTypeColor}, ${userTypeColor}CC)`,
                  color: "white",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: isLoading || selectedFiles.length === 0 ? "not-allowed" : "pointer",
                  fontFamily: '"Red Hat Display", sans-serif',
                  transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                  boxShadow: isLoading || selectedFiles.length === 0
                    ? "0 4px 12px rgba(0, 0, 0, 0.1)"
                    : `0 8px 20px ${userTypeColor}40`,
                  position: "relative",
                  overflow: "hidden",
                }}
                onMouseEnter={(e) => {
                  if (!isLoading && selectedFiles.length > 0) {
                    e.target.style.transform = "scale(1.05)";
                    e.target.style.boxShadow = `0 12px 24px ${userTypeColor}50`;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isLoading && selectedFiles.length > 0) {
                    e.target.style.transform = "scale(1)";
                    e.target.style.boxShadow = `0 8px 20px ${userTypeColor}40`;
                  }
                }}
              >
                {isLoading ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '140px', justifyContent: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '4px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                        <span>{t("addStoryModal.uploading", "Uploading")}</span>
                        <span>{uploadProgress}%</span>
                      </div>
                      <div style={{
                        width: '100%',
                        height: '4px',
                        background: 'rgba(255,255,255,0.3)',
                        borderRadius: '2px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${uploadProgress}%`,
                          height: '100%',
                          background: 'white',
                          transition: 'width 0.2s linear'
                        }} />
                      </div>
                    </div>
                  </div>
                ) : t("addStoryModal.share")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body)
}

export default AddStoryModal
